import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import QuickActionToolbar from '../../components/ui/QuickActionToolbar';
import SubscriptionTierIndicator from '../../components/ui/SubscriptionTierIndicator';
import CraneSelectionPanel from './components/CraneSelectionPanel';
import ComparisonTable from './components/ComparisonTable';
import ComparisonActions from './components/ComparisonActions';
import MobileComparisonView from './components/MobileComparisonView';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const ComparisonTool = () => {
  const [selectedCranes, setSelectedCranes] = useState([]);
  const [isMobileView, setIsMobileView] = useState(false);
  const [showMobileToggle, setShowMobileToggle] = useState(false);

  useEffect(() => {
    const checkScreenSize = () => {
      const isMobile = window.innerWidth < 1024;
      setShowMobileToggle(isMobile);
      if (isMobile && selectedCranes?.length > 0) {
        setIsMobileView(true);
      }
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, [selectedCranes?.length]);

  const breadcrumbItems = [
    { label: 'Dashboard', path: '/crane-search-dashboard' },
    { label: 'Comparison Tool' }
  ];

  const handleAddCrane = (crane) => {
    if (selectedCranes?.length < 4 && !selectedCranes?.find(c => c?.id === crane?.id)) {
      setSelectedCranes(prev => [...prev, crane]);
    }
  };

  const handleRemoveCrane = (craneId) => {
    setSelectedCranes(prev => prev?.filter(crane => crane?.id !== craneId));
  };

  const handleClearAll = () => {
    setSelectedCranes([]);
    setIsMobileView(false);
  };

  const handleGenerateReport = async () => {
    // Mock report generation
    return new Promise((resolve) => {
      setTimeout(() => {
        console.log('Generating comparison report for:', selectedCranes.map(c => `${c.make} ${c.model}`));
        // In real app, this would generate and download a PDF report
        alert('Report generated successfully! Download will start shortly.');
        resolve();
      }, 2000);
    });
  };

  const handleSaveComparison = (comparisonData) => {
    console.log('Saving comparison:', comparisonData);
    // In real app, this would save to user's account
    alert(`Comparison "${comparisonData?.name}" saved successfully!`);
  };

  const handleExportData = (format) => {
    console.log(`Exporting comparison data as ${format}`);
    // In real app, this would export data in the specified format
    alert(`Exporting data as ${format?.toUpperCase()}...`);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <QuickActionToolbar />
      <main className="container mx-auto px-6 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <Breadcrumbs items={breadcrumbItems} className="mb-2" />
              <h1 className="text-3xl font-bold text-text-primary">Crane Comparison Tool</h1>
              <p className="text-text-secondary mt-2">
                Compare up to 4 crane models side-by-side with detailed specifications, valuations, and market data.
              </p>
            </div>
            <div className="flex items-center space-x-4">
              <SubscriptionTierIndicator tier="pro" />
              {showMobileToggle && selectedCranes?.length > 0 && (
                <Button
                  variant={isMobileView ? "default" : "outline"}
                  size="sm"
                  onClick={() => setIsMobileView(!isMobileView)}
                  iconName={isMobileView ? "Monitor" : "Smartphone"}
                  iconPosition="left"
                >
                  {isMobileView ? 'Desktop View' : 'Mobile View'}
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Selection Panel - Always visible on desktop, collapsible on mobile */}
          <div className={`lg:col-span-1 ${isMobileView && selectedCranes?.length > 0 ? 'hidden lg:block' : ''}`}>
            <div className="space-y-6">
              <CraneSelectionPanel
                selectedCranes={selectedCranes}
                onAddCrane={handleAddCrane}
                onRemoveCrane={handleRemoveCrane}
                maxCranes={4}
              />
              
              {selectedCranes?.length > 0 && (
                <ComparisonActions
                  selectedCranes={selectedCranes}
                  onGenerateReport={handleGenerateReport}
                  onSaveComparison={handleSaveComparison}
                  onClearAll={handleClearAll}
                  onExportData={handleExportData}
                />
              )}
            </div>
          </div>

          {/* Comparison Display */}
          <div className="lg:col-span-3">
            {isMobileView && selectedCranes?.length > 0 ? (
              <MobileComparisonView
                selectedCranes={selectedCranes}
                onRemoveCrane={handleRemoveCrane}
              />
            ) : (
              <ComparisonTable
                selectedCranes={selectedCranes}
                onGenerateReport={handleGenerateReport}
                onSaveComparison={handleSaveComparison}
              />
            )}
          </div>
        </div>

        {/* Help Section */}
        {selectedCranes?.length === 0 && (
          <div className="mt-12 bg-card border border-border rounded-lg p-8">
            <div className="text-center max-w-2xl mx-auto">
              <Icon name="HelpCircle" size={48} className="mx-auto mb-4 text-primary" />
              <h2 className="text-xl font-semibold text-text-primary mb-4">How to Use the Comparison Tool</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-left">
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <div className="w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-medium">1</div>
                    <h3 className="font-medium text-text-primary">Search & Select</h3>
                  </div>
                  <p className="text-sm text-text-secondary">
                    Use the search panel to find and add up to 4 crane models for comparison.
                  </p>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <div className="w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-medium">2</div>
                    <h3 className="font-medium text-text-primary">Compare Specs</h3>
                  </div>
                  <p className="text-sm text-text-secondary">
                    View detailed specifications, valuations, and performance metrics side-by-side.
                  </p>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <div className="w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-medium">3</div>
                    <h3 className="font-medium text-text-primary">Generate Reports</h3>
                  </div>
                  <p className="text-sm text-text-secondary">
                    Export professional comparison reports and save for future reference.
                  </p>
                </div>
              </div>
              <div className="mt-6">
                <Button
                  variant="default"
                  onClick={() => window.location.href = '/crane-search-dashboard'}
                  iconName="Search"
                  iconPosition="left"
                >
                  Start Searching Cranes
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Feature Highlights */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            {
              icon: 'GitCompare',
              title: 'Side-by-Side Analysis',
              description: 'Compare specifications, pricing, and performance metrics across multiple crane models.'
            },
            {
              icon: 'TrendingUp',
              title: 'Market Insights',
              description: 'Access real-time market data, pricing trends, and availability information.'
            },
            {
              icon: 'FileText',
              title: 'Professional Reports',
              description: 'Generate branded PDF reports with detailed comparisons and legal disclaimers.'
            },
            {
              icon: 'Bookmark',
              title: 'Save Comparisons',
              description: 'Save your comparisons for future reference and share with team members.'
            }
          ]?.map((feature, index) => (
            <div key={index} className="bg-card border border-border rounded-lg p-6 text-center">
              <Icon name={feature?.icon} size={32} className="mx-auto mb-3 text-primary" />
              <h3 className="font-medium text-text-primary mb-2">{feature?.title}</h3>
              <p className="text-sm text-text-secondary">{feature?.description}</p>
            </div>
          ))}
        </div>
      </main>
      {/* Footer */}
      <footer className="bg-muted/30 border-t border-border mt-16">
        <div className="container mx-auto px-6 py-8">
          <div className="flex items-center justify-between">
            <div className="text-sm text-text-secondary">
              © {new Date()?.getFullYear()} CranePrice. Professional crane valuation and comparison tools.
            </div>
            <div className="flex items-center space-x-4 text-sm text-text-secondary">
              <span>Powered by industry-leading data</span>
              <Icon name="Shield" size={16} />
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ComparisonTool;